import { useEffect, useRef, useState } from "react";
import { Send, MessageCircle, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const CHANNEL_LABEL = { whatsapp: "WhatsApp", web_chat: "Web" };

function bubbleBg(senderType) {
  if (senderType === "client") return "zenith-cta text-white";
  if (senderType === "coach") return "bg-primary-fixed text-primary-on-container";
  return "bg-surface-container-lowest text-on-surface";
}

function senderLabel(senderType) {
  if (senderType === "bot") return "Assistant";
  if (senderType === "coach") return "Coach";
  return "You";
}

function Bubble({ m }) {
  const isClient = m.senderType === "client";
  const isSystem = m.senderType === "system";

  if (isSystem) {
    return (
      <div className="flex justify-center my-3" data-testid="chat-system-message">
        <div className="glass-blush rounded-2xl px-4 py-2.5 max-w-[85%] text-center">
          <p className="text-sm text-secondary-on-container">{m.messageText}</p>
        </div>
      </div>
    );
  }

  const align = isClient ? "items-end" : "items-start";
  const bg = bubbleBg(m.senderType);

  return (
    <div className={`flex flex-col ${align} my-1.5`} data-testid="chat-message">
      <div className={`${bg} rounded-2xl px-4 py-2.5 max-w-[82%] shadow-ambient`}>
        <p className="text-sm leading-relaxed whitespace-pre-wrap">{m.messageText}</p>
      </div>
      <div className="flex items-center gap-1.5 mt-1 px-1">
        {m.channel === "whatsapp" ? (
          <Smartphone className="w-3 h-3 text-outline" strokeWidth={1.5} />
        ) : (
          <MessageCircle className="w-3 h-3 text-outline" strokeWidth={1.5} />
        )}
        <span className="text-[10px] text-outline">
          {senderLabel(m.senderType)} · {CHANNEL_LABEL[m.channel] || m.channel}
        </span>
      </div>
    </div>
  );
}

export default function ChatConversation({ compact = false }) {
  const [messages, setMessages] = useState([]);
  const [quickReplies, setQuickReplies] = useState([]);
  const [escalated, setEscalated] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    const res = await api.get("/chat/conversation");
    setMessages(res.data.messages);
    setQuickReplies(res.data.quickReplies || []);
    setEscalated(res.data.conversation?.isEscalated || false);
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async (value, channel = "web_chat") => {
    const content = value ?? text;
    if (!content.trim() || sending) return;
    setSending(true);
    const optimistic = {
      message_id: `tmp_${Date.now()}`,
      senderType: "client",
      channel,
      messageText: content,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    setText("");
    try {
      const endpoint = channel === "whatsapp" ? "/chat/simulate-whatsapp" : "/chat/message";
      const res = await api.post(endpoint, { messageText: content, channel });
      if (res.data.escalated) setEscalated(true);
      await load();
    } catch (e) {
      setMessages((prev) => prev.filter((m) => m.message_id !== optimistic.message_id));
      setText(content);
      toast.error("Message couldn't be sent. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {escalated && (
        <div className="glass-blush rounded-2xl px-4 py-3 mb-3 flex items-center gap-2" data-testid="escalation-banner">
          <span className="w-2 h-2 rounded-full bg-secondary animate-pulse" />
          <p className="text-sm text-secondary-on-container">Connected to your coach — they'll reply here & on WhatsApp.</p>
        </div>
      )}

      <div ref={scrollRef} className={`flex-1 overflow-y-auto no-scrollbar pr-1 ${compact ? "max-h-[46vh]" : "min-h-[52vh]"}`}>
        {messages.length === 0 && (
          <div className="text-center py-10">
            <p className="font-headline text-primary text-lg">Hi there 🌿</p>
            <p className="text-sm text-on-surface-variant mt-1">Ask me anything, or pick a quick reply below.</p>
          </div>
        )}
        {messages.map((m) => (
          <Bubble key={m.message_id} m={m} />
        ))}
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar py-3">
        {quickReplies.map((q) => (
          <button
            key={q}
            onClick={() => send(q)}
            className="whitespace-nowrap text-sm px-4 py-2 rounded-full bg-surface-container-low text-primary hover:bg-primary-fixed transition-colors duration-300"
            data-testid="quick-reply"
          >
            {q}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Type a message…"
          className="flex-1 bg-surface-container-low rounded-2xl px-4 py-3.5 text-sm outline-none focus:bg-primary-fixed transition-colors duration-500"
          data-testid="chat-input"
        />
        <button
          onClick={() => send(text, "whatsapp")}
          title="Simulate sending via WhatsApp"
          className="w-12 h-12 rounded-2xl bg-surface-container flex items-center justify-center text-primary hover:bg-primary-fixed transition-colors duration-300 shrink-0"
          data-testid="whatsapp-send-button"
        >
          <Smartphone className="w-5 h-5" strokeWidth={1.5} />
        </button>
        <button
          onClick={() => send()}
          disabled={sending}
          className="w-12 h-12 rounded-2xl zenith-cta flex items-center justify-center text-white hover:opacity-90 transition-opacity duration-300 shrink-0 disabled:opacity-50"
          data-testid="chat-send-button"
        >
          <Send className="w-5 h-5" strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}
